import {t} from 'i18next';
import {Step} from '../common/arr-stepper/ArrStepper';
import {ITagsList} from '../../api/types/tagFoldering.type';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../shared/interfaces';
import {GridColDef, GridColumnHeaderParams} from '@mui/x-data-grid';
import {FolderNode} from '../../api/types/documentPrinting.type';

export type StepKey =
  | 'sourceSelection'
  | 'basisPrinting'
  | 'analysis'
  | 'exceptionFileTypes'
  | 'slipSheets'
  | 'branding'
  | 'exportFormat';

export const STEP_KEY_SOURCE_SELECTION: StepKey = 'sourceSelection';
export const STEP_KEY_BASIS_PRINTING: StepKey = 'basisPrinting';
export const STEP_KEY_ANALYSIS: StepKey = 'analysis';
export const STEP_KEY_EXCEPTION_FILE_TYPES: StepKey = 'exceptionFileTypes';
export const STEP_KEY_SLIP_SHEETS: StepKey = 'slipSheets';
export const STEP_KEY_BRANDING: StepKey = 'branding';
export const STEP_KEY_EXPORT_FORMAT: StepKey = 'exportFormat';

export const batchPrintStepperItems = () => {
  return [
    {
      key: STEP_KEY_SOURCE_SELECTION,
      label: t('batchPrint.sourceSelection.title'),
      state: 'current',
    },
    {
      key: STEP_KEY_BASIS_PRINTING,
      label: t('batchPrint.basisPrinting'),
      state: 'future',
    },
    {
      key: STEP_KEY_ANALYSIS,
      label: t('batchPrint.analysis'),
      state: 'future',
    },
    {
      key: STEP_KEY_EXCEPTION_FILE_TYPES,
      label: t('batchPrint.exceptionFileTypes'),
      state: 'future',
    },
    {
      key: STEP_KEY_SLIP_SHEETS,
      label: t('batchPrint.slipSheets'),

      state: 'future',
    },
    {
      key: STEP_KEY_BRANDING,
      label: t('batchPrint.branding'),

      state: 'future',
    },
    {
      key: STEP_KEY_EXPORT_FORMAT,
      label: t('batchPrint.exportFormat'),
      state: 'future',
    },
  ] as Step[];
};

export const buildTree = (items: ITagsList[] | FolderNode[] = []) => {
  const nodeMap: any = {};
  items.forEach((it: ITagsList | FolderNode) => {
    nodeMap[it.id] = {
      ...it,
      label: it.text,
      fileType: it.isGroup && (it.type === 'tags' ? 'tag' : 'folder'),
      skipParent: it.isGroup,
    };
    delete nodeMap[it.id].children;
  });

  const roots: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
  items.forEach((it: ITagsList | FolderNode) => {
    const node = nodeMap[it.id];
    const parentId = it.parent;
    if (!parentId || !nodeMap[parentId]) roots.push(node);
    else {
      const parent = nodeMap[parentId];
      if (!parent.children) parent.children = [];
      parent.children.push(node);
    }
  });

  Object.values(nodeMap).forEach((n: any) => {
    n.hasChildren = Array.isArray(n.children) && n.children.length > 0;
  });

  return roots;
};

export const findNodeById = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  id: string
): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

export function getParentIds(data, childId) {
  const map = {};

  function buildMap(nodes: any) {
    for (const node of nodes) {
      map[node.id] = node.parent;

      if (node.children) buildMap(node.children, node.id);
    }
  }

  buildMap(data);

  const parents = [];

  let current = childId;

  while (map[current] && map[current] !== '#') {
    parents.push(map[current]);

    current = map[current];
  }

  return parents.reverse();
}

export function getFirstParentWithChildren(data, childId) {
  const parentMap = {};

  const nodeMap = {};

  function buildMap(nodes, parent = null) {
    for (const node of nodes) {
      parentMap[node.id] = parent;

      nodeMap[node.id] = node;

      if (node.children) {
        buildMap(node.children, node.id);
      }
    }
  }

  buildMap(data);

  // CASE 1: childId NOT provided top-down search

  if (!childId) {
    for (const id in nodeMap) {
      const node = nodeMap[id];

      if (
        node?.isGroup ||
        node?.hasChildren === true ||
        (node?.children && node.children.length > 0)
      ) {
        return id;
      }
    }

    return null;
  }

  // CASE 2: childId provided same logic as before

  const parents = [];

  let current = childId;

  while (parentMap[current] && parentMap[current] !== '#') {
    parents.push(parentMap[current]);

    current = parentMap[current];
  }

  for (const parentId of parents.reverse()) {
    const parentNode = nodeMap[parentId];

    if (
      parentNode?.hasChildren === true ||
      (parentNode?.children && parentNode.children.length > 0)
    ) {
      return parentId;
    }
  }

  return null;
}

export const analysisColumns: GridColDef[] = [
  {
    field: 'DOC_ID',
    headerName: t('batchPrint.analysisGroup.grid.docID'),
    flex: 1,
    sortable: false,
    renderHeader: (param: GridColumnHeaderParams) => (
            <div
              tabIndex={0}
              role="rowheader"
              className="table_header"
              data-testid={`batchPrint.analysisGroup.grid.header`}
            >
              {param.colDef.headerName}
            </div>
          ),
    renderCell: (params: any) => (
          <div
              role="presentation"
              tabIndex={0}
              data-testid={'batchPrint.analysisGroup.grid.row'}
            >
            <span>{params.row.DOC_ID}</span>
            </div>
      ),
  },

  {
    field: 'NOT_IN_ANY_PRODUCTION',
    headerName: t('batchPrint.analysisGroup.grid.notInAny'),
    flex: 1,
    sortable: false,
    renderHeader: (param: GridColumnHeaderParams) => (
            <div
              tabIndex={0}
              role="rowheader"
              data-testid={'batchPrint.analysisGroup.grid.header'}
            >
              {param.colDef.headerName}
            </div>
          ),
    renderCell: (params: any) => (
            <div 
              role="presentation"
              tabIndex={0}
              data-testid={'batchPrint.analysisGroup.grid.row'}
            >
              
             <span> {params.row.NOT_IN_ANY_PRODUCTION} </span>
            </div>
          ),
  },
];

export const EXCLUDED_FIELDS = [
  'DOC',
  'DOC_ID',
  'NOT_IN_ANY',
  'NOT_IN_ANY_PRODUCTION',
  'SKIP_PRINTING',
];
function getFocusableElements(root: ParentNode = document) {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.tabIndex >= 0 && !el.hasAttribute("disabled")
  );
}

export const focusNextOutside = (wrapper: HTMLElement, backwards: boolean) => {
  const all = getFocusableElements(document);
  const inside = new Set(getFocusableElements(wrapper));

  const active = document.activeElement as HTMLElement | null;
  const activeIndex = all.findIndex((el) => el === active);
  if (activeIndex === -1) return;

  if (!backwards) {
    for (let i = activeIndex + 1; i < all.length; i++) {
      if (!inside.has(all[i])) {
        all[i].focus();
        return;
      }
    }
  } else {
    for (let i = activeIndex - 1; i >= 0; i--) {
      if (!inside.has(all[i])) {
        all[i].focus();
        return;
      }
    }
  }
}
